import { blockSlice, widgetInstanceActions } from '../slices';

import type { RootState } from '../redux-store';
import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';

type AppThunk<R = void> = ThunkAction<R, RootState, unknown, UnknownAction>;

/**
 * Toggle visibility of newCases aside widgets within the mainGeoMap block
 * Uses additionalInstances feature to show/hide widgets alongside geomap
 *
 * When enabling aside mode:
 * - Shows new-cases widgets as asides in the mainGeoMap block
 * - Seals standalone new-cases widgets (hides them but keeps them in state)
 *
 * When disabling aside mode:
 * - Hides new-cases asides from mainGeoMap block
 * - Unseals standalone new-cases widgets (shows them again)
 *
 * This preserves widget state and section positions, unlike the old approach
 * which removed widgets entirely.
 */
export const toggleNewCasesBlocks =
  (visible: boolean): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const blockEntities = state.blocks.entities;
    const widgetEntities = state.widgetInstances.entities;
    const sectionEntities = state.sections.entities;

    const centerSection = sectionEntities.center;
    if (!centerSection) {
      return;
    }

    const mainGeoMapBlock =
      blockEntities['geo-map-main'] ||
      centerSection.blockOrder
        .map(blockId => blockEntities[blockId])
        .find(
          block =>
            block &&
            (block.instanceId.startsWith('geo-map') ||
              widgetEntities[block.instanceId]?.type === 'geo-map'),
        );

    if (!mainGeoMapBlock) {
      return;
    }

    // Get or create the mainNewCases instance (ID: 'new-cases-aside')
    let mainNewCasesInstance = widgetEntities['new-cases-aside'];

    if (!mainNewCasesInstance) {
      dispatch(
        widgetInstanceActions.setWidgetInstance({
          id: 'new-cases-aside',
          title: 'New Cases',
          sectionId: null, // Not in any section, only used as aside
          type: 'new-cases',
          settings: {}, // Use default settings
        }),
      );

      // Get the newly created instance
      const newState = dispatch((_, getState) =>
        getState(),
      ) as unknown as RootState;
      mainNewCasesInstance =
        newState.widgetInstances.entities['new-cases-aside'];

      if (!mainNewCasesInstance) {
        return;
      }
    }

    // Seal/unseal standalone new-cases widgets based on aside visibility
    // When aside is visible (enabled), seal standalone widgets to hide them
    // When aside is hidden (disabled), unseal standalone widgets to show them
    Object.values(widgetEntities).forEach(widget => {
      if (
        widget &&
        widget.type === 'new-cases' &&
        widget.id !== mainNewCasesInstance.id &&
        widget.sectionId !== null // Only affect widgets that are in sections
      ) {
        dispatch(
          widgetInstanceActions.setWidgetInstanceSealed({
            id: widget.id,
            sealed: visible, // Seal when aside is visible, unseal when aside is hidden
          }),
        );
      }
    });

    // Update the mainGeoMap block to add/remove additionalInstances
    const additionalInstances = visible
      ? [
          {
            instanceId: mainNewCasesInstance.id,
            instanceIndex: 0,
            position: 'left' as const,
          },
          {
            instanceId: mainNewCasesInstance.id,
            instanceIndex: 1,
            position: 'right' as const,
          },
        ]
      : [];

    dispatch(
      blockSlice.actions.blocksUpdate([
        {
          id: mainGeoMapBlock.id,
          changes: {
            additionalInstances,
          },
        },
      ]),
    );
  };
